import React from 'react';
import { useVoiceStatus, VoiceMode } from '../hooks/useVoiceStatus';
import type { ActiveMind } from '../services/api';
import { isRuntimeRouted, type RuntimeStatusView } from '../hooks/useRuntimeStatus';

const MODE_CONFIG: Record<VoiceMode, { emoji: string; label: string; color: string; title: string }> = {
  voice:          { emoji: '🧠', label: 'Voice: LoRA',     color: '#22c55e', title: 'Hannah\'s trained voice model is writing her words. Click to switch back to Claude (the default).' },
  claude:         { emoji: '🤖', label: 'Voice: Claude',   color: '#6366f1', title: 'Claude writes her words (the default). Click to switch the voice model on.' },
  'error-fallback': { emoji: '⚠️', label: 'Voice: Fallback', color: '#f59e0b', title: 'Voice model auto-disabled due to errors. Click to retry.' },
  disabled:       { emoji: '',   label: '',               color: '',        title: '' },
};

interface VoiceToggleProps {
  token: string | null;
  mind: ActiveMind;
  /** Hannah's runtime status (creator only; null otherwise). Decides whether the legacy voice setting applies at all. */
  runtime?: RuntimeStatusView | null;
}

export const VoiceToggle: React.FC<VoiceToggleProps> = ({ token, mind, runtime = null }) => {
  const modelRef = React.useRef<HTMLDetailsElement>(null);
  React.useEffect(() => {
    const outside = (event: PointerEvent) => {
      if (modelRef.current && !modelRef.current.contains(event.target as Node)) modelRef.current.open = false;
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && modelRef.current?.open) {
        modelRef.current.open = false;
        modelRef.current.querySelector('summary')?.focus();
      }
    };
    document.addEventListener('pointerdown', outside);
    document.addEventListener('keydown', escape);
    return () => { document.removeEventListener('pointerdown', outside); document.removeEventListener('keydown', escape); };
  }, []);
  const routed = mind === 'hannah' && isRuntimeRouted(runtime);
  const { status, isLoading, isToggling, toggle } = useVoiceStatus(token, mind, !routed);

  // Routed to the runtime: the legacy voice setting has no effect there, so it is neither shown nor changeable.
  if (routed && runtime?.kind === 'ok') {
    const model = runtime.configuredModel ?? runtime.lastReplyModel;
    const label = model ? `${model}${runtime.configuredModel ? '' : ' (last reply)'}` : 'unavailable';
    return (
      <details className="header-model" ref={modelRef}>
        <summary className="header-btn">Model</summary>
        <div className="header-model-panel">
          <span className="voice-retired">Model: {label}</span>
          <p>This is the model used by Hannah’s runtime. Her records and activity persist separately.</p>
        </div>
      </details>
    );
  }
  if (routed) {
    return <span className="voice-retired" title="Her chat is routed to the runtime; its status could not be read.">Model: unavailable</span>;
  }

  // A retired voice is shown as unavailable for THIS mind rather than hidden, so the header never
  // implies another mind's control applies here.
  if (status?.retired) {
    return (
      <span className="voice-retired" title={status.reason ?? 'Voice model retired for this mind'}>Voice: retired</span>
    );
  }

  // Don't render if voice model isn't deployed or still loading
  if (isLoading || !status || status.mode === 'disabled') {
    return null;
  }

  const config = MODE_CONFIG[status.mode];

  return (
    <button
      className={`voice-toggle voice-toggle--${status.mode}`}
      onClick={toggle}
      disabled={isToggling}
      title={config.title}
      style={{ '--voice-color': config.color } as React.CSSProperties}
    >
      <span className="voice-toggle__emoji">{config.emoji}</span>
      <span className="voice-toggle__label">{config.label}</span>
      {status.errorCount > 0 && status.mode === 'error-fallback' && (
        <span className="voice-toggle__errors">({status.errorCount})</span>
      )}
      {isToggling && <span className="voice-toggle__spinner" />}
    </button>
  );
};
